// ============================================================================
// Field tab rendered over the document — the direct fix for "I clicked and
// nothing happened".
//
// Every assignable field is a real <button> with a filled, bounded tab, a type
// icon, a short label, a pointer cursor, a hover state and a strong focused
// state. Required/optional/complete are each signalled by SHAPE AND TEXT as
// well as colour, never colour alone.
// ============================================================================

import { forwardRef } from 'react';
import {
  Calendar, Check, CheckSquare, PenLine, Signature, Square, Type as TypeIcon,
  User,
} from 'lucide-react';
import type { FieldType } from './envelope-lib';

export const FIELD_LABEL: Record<FieldType, string> = {
  signature: 'Sign here',
  initials: 'Initial',
  date: 'Date',
  name: 'Full name',
  email: 'Email',
  text: 'Text',
  checkbox: 'Check',
};

const FIELD_ICON: Record<FieldType, typeof PenLine> = {
  signature: Signature,
  initials: PenLine,
  date: Calendar,
  name: User,
  email: TypeIcon,
  text: TypeIcon,
  checkbox: Square,
};

export interface SignerFieldTabProps {
  fieldType: FieldType;
  required: boolean;
  done: boolean;
  isCurrent: boolean;
  /** Rendered content once complete: text, or a PNG data URL. */
  value: string | boolean;
  /** Absolute placement, already resolved to CSS pixels. */
  style: React.CSSProperties;
  /** 1-based position for screen readers, e.g. "field 2 of 6". */
  index: number;
  total: number;
  onActivate: () => void;
}

export const SignerFieldTab = forwardRef<HTMLButtonElement, SignerFieldTabProps>(
  function SignerFieldTab(
    { fieldType, required, done, isCurrent, value, style, index, total, onActivate },
    ref,
  ) {
    const Icon = fieldType === 'checkbox' && value === true ? CheckSquare : FIELD_ICON[fieldType];
    const label = FIELD_LABEL[fieldType];
    const isImage = typeof value === 'string' && value.startsWith('data:image/');

    // Colour is only ever a reinforcement. The state is also carried by the
    // icon (check vs type glyph), the asterisk, and the accessible name.
    const tone = done
      ? 'border-st-ok-border-strong bg-st-ok-bg text-st-ok-fg-strong'
      : required
        ? 'border-st-danger-border-strong bg-st-warn-bg text-st-warn-fg-strong'
        : 'border-tcs-border bg-tcs-bg-muted text-tcs-text-muted';

    const accessibleName = [
      label,
      required ? 'required' : 'optional',
      done ? 'completed' : 'not yet completed',
      `field ${index} of ${total}`,
    ].join(', ');

    return (
      <button
        ref={ref}
        type="button"
        onClick={onActivate}
        aria-label={accessibleName}
        aria-current={isCurrent ? 'step' : undefined}
        data-esign-field
        className={`absolute flex cursor-pointer items-center gap-1 overflow-hidden rounded-sm border-2 px-1 text-left text-[11px] font-medium leading-tight shadow-sm transition hover:brightness-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-tcs-accent-ink/40 ${tone} ${
          isCurrent ? 'ring-4 ring-tcs-accent-ink/50' : ''
        }`}
        style={style}
      >
        {done && isImage ? (
          <img
            src={value as string}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <>
            {done
              ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              : <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            <span className="truncate">
              {done
                ? (fieldType === 'checkbox'
                    ? (value === true ? 'Checked' : 'Unchecked')
                    : String(value))
                : label}
              {!done && required && (
                <span className="ml-0.5 font-bold text-st-danger-fg" aria-hidden>*</span>
              )}
            </span>
          </>
        )}
      </button>
    );
  },
);
